import { CommonModule, DatePipe } from '@angular/common';
import { NoteDisplayTitlePipe } from '../../_util/note-display-title.util';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SliderModule } from 'primeng/slider';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';
import { AppConfig } from '../user-settings/app.config';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { RichEditorComponent } from '../rich-editor/rich-editor.component';
import { BulkActionDialogComponent } from '../home/bulk-action-dialog/bulk-action-dialog.component';
import { NoteViewBase } from './note-view-base';
import { DataService } from '../../_service/data.service';
import type { SimilarNoteResult } from '../../_service/data.service';
import { AlertService } from '../../_service/alert.service';
import { SnackbarService } from '../../_service/snackbar.service';
import { EDITOR_SURFACE_CLASSES } from './note.styles';
import { NoteShellComponent } from './note-shell.component';
import { NoteHeaderComponent } from './note-header.component';
import { NotePanelDirective } from './note-panel.directive';
import { NoteEmptyStateComponent } from './note-empty-state.component';
import { ImageLightboxComponent, LightboxImage } from '../../_shared/ui/image-lightbox/image-lightbox.component';
import { imageCropLayout } from '../rich-editor/extensions/image-crop.util';
import { NOTE_TAB_STATE_KEY, NoteTab, initialNoteTab } from './note.types';
import { NOTE_SCROLL_STATE_KEY, captureNoteScrollAnchor, readNoteScrollAnchor, restoreNoteScrollAnchor } from './note-scroll-anchor';

/**
 * The View Note page (`/view/:_id`).
 *
 * It extends `NoteViewBase` on purpose: note loading, offline/IndexedDB access, tags, related and
 * similar notes, AI summary, favourites, zoom, print/PDF/DOCX/JSON export, copy, delete and the page
 * menu are all inherited, so no persistence, sync or data-access logic is duplicated. This class only
 * supplies the template, built from the reusable note shell.
 */
@Component({
  selector: 'app-note-view',
  standalone: true,
  templateUrl: './note-view.component.html',
  // note-view-page: scopes the print layout in styles.scss (@media print).
  host: { class: 'block h-dvh note-view-page' },
  // The parent's providers are not inherited by subclasses, and its constructor needs them.
  providers: [DatePipe, ConfirmationService],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    ConfirmDialogModule,
    SliderModule,
    TieredMenuModule,
    TooltipModule,
    AppConfig,
    FileUploadComponent,
    RichEditorComponent,
    BulkActionDialogComponent,
    NoteShellComponent,
    NoteHeaderComponent,
    NotePanelDirective,
    NoteEmptyStateComponent,
    NoteDisplayTitlePipe,
    ImageLightboxComponent,
  ],
})
export class NoteViewComponent extends NoteViewBase implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly noteRouter = inject(Router);
  private readonly noteData = inject(DataService);
  private readonly noteAlerts = inject(AlertService);
  private readonly noteSnackbar = inject(SnackbarService);

  /** Currently selected tab. The Note tab (reading) is the default. */
  activeNoteTab = 'note';

  private tabsCacheKey = '';
  private tabsCache: NoteTab[] = [];

  @ViewChild(NoteShellComponent) private noteShell?: NoteShellComponent;
  /** The Note tab's status/tags/reading-tools row — hidden in Focus Mode; its height is used to keep
   * the reading position stable when it's hidden/restored. */
  @ViewChild('focusToolsRow') private focusToolsRow?: ElementRef<HTMLElement>;
  /** The note title shown in its place while in Focus Mode (the header with the title is hidden). */
  @ViewChild('focusTitle') private focusTitle?: ElementRef<HTMLElement>;

  // ===================== Focus Mode (Note tab reading mode) =====================
  // Engages on a tap with a finger on any screen (phones, iPad in either orientation, touch laptops),
  // and on any click below the same `lg` breakpoint used elsewhere in the app (e.g. ListComponent).
  // A mouse click on a desktop-sized layout never engages it.
  private static readonly FOCUS_MODE_MOBILE_QUERY = '(max-width: 63.9375rem)'; // below Tailwind's `lg` (64rem)
  private readonly focusModeMediaQuery: MediaQueryList = window.matchMedia(
    NoteViewComponent.FOCUS_MODE_MOBILE_QUERY,
  );
  private readonly onFocusModeMediaChange = (event: MediaQueryListEvent): void => {
    this.isMobileViewport = event.matches;
    if (!this.isMobileViewport && !this.focusModeByTouch) this.exitFocusMode();
  };
  private isMobileViewport = this.focusModeMediaQuery.matches;
  /** The pointer of the latest press in the content area was a finger (see onNoteContentPointerDown). */
  private lastPressWasTouch = false;
  /** Focus Mode was entered by a finger tap — it then stays on when the layout widens (iPad rotated). */
  private focusModeByTouch = false;

  /** True while the note content fills the viewport with all chrome hidden. */
  focusMode = false;
  /** True while the small "Exit Focus Mode" overlay is showing (auto-hides itself). */
  showFocusExitOverlay = false;
  private focusExitOverlayTimer: ReturnType<typeof setTimeout> | null = null;

  /** Interactive content that must never enter/exit Focus Mode on tap: links, images, form controls,
   * and collapsible-section titles (which toggle open/closed on click even in read-only mode). */
  private static readonly FOCUS_MODE_INTERACTIVE_SELECTOR =
    'a, button, input, textarea, select, label, [role="button"], [contenteditable="true"], ' +
    '[data-type="collapsible-title"], .tiptap-image-node, .p-slider, .p-slider *';

  override ngOnInit(): void {
    super.ngOnInit();
    // The tab carried over from Edit (same note), else a ?tab= deep link, else Note.
    this.activeNoteTab = initialNoteTab(history.state, this.activatedRoute.snapshot.queryParamMap.get('tab'));
    this.focusModeMediaQuery.addEventListener('change', this.onFocusModeMediaChange);
    // Coming from Edit of this note: open at the same reading position.
    const anchor = readNoteScrollAnchor(history.state);
    if (anchor && this.activeNoteTab === 'note') {
      this.stopScrollRestore = restoreNoteScrollAnchor(() => this.noteShell?.scrollElement ?? null, anchor);
    }
  }

  private stopScrollRestore: (() => void) | null = null;

  override ngOnDestroy(): void {
    this.stopScrollRestore?.();
    this.focusModeMediaQuery.removeEventListener('change', this.onFocusModeMediaChange);
    this.clearFocusExitOverlayTimer();
    super.ngOnDestroy();
  }

  /**
   * Tap handler for the Note tab's content area. Enters Focus Mode on a qualifying tap; while
   * already in Focus Mode, a qualifying tap reveals (or re-arms the auto-hide timer of) the small
   * "Exit Focus Mode" overlay instead. Taps on interactive content, or that end a text selection,
   * are ignored so links/images/controls/selecting text keep working normally.
   */
  onNoteContentPointerDown(event: PointerEvent): void {
    this.lastPressWasTouch = event.pointerType === 'touch';
  }

  onNoteContentTap(event: MouseEvent): void {
    const byTouch = this.lastPressWasTouch;
    this.lastPressWasTouch = false;
    if (this.activeNoteTab !== 'note') return;

    const target = event.target as HTMLElement | null;
    // An image in the note (click or tap, any screen) opens it zoomed to fit the window.
    const image = target?.closest('img') as HTMLImageElement | null;
    if (image && !image.closest('a') && (image.currentSrc || image.src)) {
      this.openImageZoom(image);
      return;
    }

    if (!this.isMobileViewport && !byTouch) return;
    if (target?.closest(NoteViewComponent.FOCUS_MODE_INTERACTIVE_SELECTOR)) return;

    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && selection.toString().length > 0) return;

    if (this.focusMode) {
      this.showFocusExitOverlayFor(3500);
    } else {
      this.focusModeByTouch = byTouch;
      this.enterFocusMode();
    }
  }

  // ===================== Image zoom (lightbox) =====================
  /** The image shown zoomed over the dimmed page, or null. */
  zoomedImage: LightboxImage | null = null;

  private openImageZoom(image: HTMLImageElement): void {
    const naturalWidth = image.naturalWidth || Number(image.getAttribute('data-natural-width')) || image.width || 1;
    const naturalHeight = image.naturalHeight || Number(image.getAttribute('data-natural-height')) || image.height || 1;
    this.zoomedImage = {
      src: image.currentSrc || image.src,
      alt: image.alt || '',
      naturalWidth,
      naturalHeight,
      // A cropped image is shown cropped here too.
      crop: imageCropLayout(image.getAttribute('data-crop'), naturalWidth, naturalHeight),
    };
  }

  /** Closes the zoomed image (a tap/click on it that isn't a drag or pinch, its × button, or Escape). */
  closeImageZoom(): void {
    this.zoomedImage = null;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.zoomedImage) this.closeImageZoom();
  }

  exitFocusModeFromButton(event: Event): void {
    event.stopPropagation();
    this.exitFocusMode();
  }

  private enterFocusMode(): void {
    if (this.focusMode) return;
    const rowHeight = this.focusToolsRow?.nativeElement.offsetHeight ?? 0;
    const scrollBefore = this.noteShell?.scrollTop ?? 0;
    this.focusMode = true;
    this.showFocusExitOverlay = false;
    this.clearFocusExitOverlayTimer();
    // Wait for the status/tags/tools row to leave the DOM (and the title to take its place) before
    // compensating scroll, so the text being read stays where it is.
    requestAnimationFrame(() => {
      const titleHeight = this.focusTitle?.nativeElement.offsetHeight ?? 0;
      if (this.noteShell) this.noteShell.scrollTop = Math.max(0, scrollBefore - rowHeight + titleHeight);
    });
  }

  private exitFocusMode(): void {
    if (!this.focusMode) return;
    const scrollBefore = this.noteShell?.scrollTop ?? 0;
    const titleHeight = this.focusTitle?.nativeElement.offsetHeight ?? 0;
    this.focusMode = false;
    this.focusModeByTouch = false;
    this.showFocusExitOverlay = false;
    this.clearFocusExitOverlayTimer();
    // Wait for the row to re-render AND settle (fonts/icons can still shift its height one frame
    // later) before reading its height and compensating scroll.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const rowHeight = this.focusToolsRow?.nativeElement.offsetHeight ?? 0;
        if (this.noteShell) this.noteShell.scrollTop = Math.max(0, scrollBefore + rowHeight - titleHeight);
      });
    });
  }

  private showFocusExitOverlayFor(ms: number): void {
    this.showFocusExitOverlay = true;
    this.clearFocusExitOverlayTimer();
    this.focusExitOverlayTimer = setTimeout(() => {
      this.showFocusExitOverlay = false;
      this.focusExitOverlayTimer = null;
    }, ms);
  }

  private clearFocusExitOverlayTimer(): void {
    if (this.focusExitOverlayTimer !== null) {
      clearTimeout(this.focusExitOverlayTimer);
      this.focusExitOverlayTimer = null;
    }
  }

  /**
   * Tab definitions with live counts. The array is re-created only when a count changes, so the
   * shell (OnPush) is not invalidated by every change-detection pass.
   */
  get noteTabs(): NoteTab[] {
    const key = `${this.attachmentCount}|${this.relatedNotes.length}`;
    if (key !== this.tabsCacheKey) {
      this.tabsCacheKey = key;
      this.tabsCache = [
        { id: 'note', label: 'Note' },
        { id: 'properties', label: 'Properties' },
        { id: 'attachments', label: 'Attachments', count: this.attachmentCount },
        { id: 'related', label: 'Related', count: this.relatedNotes.length },
        { id: 'comments', label: 'Comments' },
        { id: 'history', label: 'History' },
      ];
    }
    return this.tabsCache;
  }

  protected readonly editorSurfaceClasses = EDITOR_SURFACE_CLASSES;

  /** Whether "Find similar" has been run, to tell "not searched yet" from "searched, nothing found". */
  similarRequested = false;

  /** "Edit" switches to the Edit page: the header/tabs are the same in both modes, so the switch feels like a mode toggle. */
  override pageMenu(selectedItem: string): void {
    if (selectedItem === 'Edit' && this.note?._id) {
      // Same tab in Edit as here (see initialNoteTab).
      void this.noteRouter.navigate(['edit', this.note._id], {
        state: {
          filter: this.filter,
          homeState: this.homeState,
          [NOTE_TAB_STATE_KEY]: this.activeNoteTab,
          // Same reading position in Edit (see note-scroll-anchor.ts).
          [NOTE_SCROLL_STATE_KEY]: this.activeNoteTab === 'note' ? captureNoteScrollAnchor(this.noteShell?.scrollElement) : null,
        },
      });
      return;
    }
    super.pageMenu(selectedItem);
  }

  /** New note / copy open in the Edit page, using this page's own data/alert services. */
  override clickNew(): void {
    this.loadingCounter++;
    this.noteData.createNote().subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data) {
          this.openInEdit(response.data._id);
        } else {
          this.noteAlerts.warn(response.error?.message || 'Failed to create new note.', { life: 4000 });
        }
        this.loadingCounter--;
      },
      error: (err) => {
        console.error('❌ clickNew(): unexpected error:', err);
        this.noteAlerts.error('An unexpected error occurred while creating a new note.', { life: 5000 });
        this.loadingCounter--;
      },
    });
  }

  override createCopyOfNote(): void {
    if (!this.note) {
      this.noteSnackbar.showErrorMessage('Note is not available to copy.');
      return;
    }
    this.loadingCounter++;
    this.noteData.createNoteCopy(this.note).subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data) {
          this.openInEdit(response.data._id);
        } else {
          this.noteAlerts.warn(response.error?.message || 'Failed to create note copy.', { life: 4000 });
        }
        this.loadingCounter--;
      },
      error: (err) => {
        console.error('❌ createCopyOfNote(): unexpected error:', err);
        this.noteAlerts.error('An unexpected error occurred while creating a note copy.', { life: 5000 });
        this.loadingCounter--;
      },
    });
  }

  private openInEdit(noteId: string): void {
    void this.noteRouter.navigate(['/edit', noteId], {
      state: { filter: this.filter, homeState: this.homeState },
    });
  }

  override findSimilarNotes(): void {
    this.similarRequested = true;
    super.findSimilarNotes();
  }

  similarNoteTitle(item: SimilarNoteResult): string {
    return String(item.title ?? (item as Record<string, unknown>)['Title'] ?? '') || '(Untitled)';
  }

  /** True when the (rich-text) comments field holds real content, not just empty markup. */
  get hasComments(): boolean {
    const html = String(this.note?.CommentsHtml ?? '');
    if (/<(img|table|hr)\b/i.test(html)) return true;
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
  }

  get hasDescription(): boolean {
    const html = String(this.note?.DescriptionHtml ?? '');
    if (/<(img|table|hr)\b/i.test(html)) return true;
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
  }

  /** The one-sentence summary and the paragraph are often the same text; show the paragraph only when it adds something. */
  get aiParagraphIfDistinct(): string {
    const summary = this.aiSummary;
    const paragraph = (summary?.summary_paragraph ?? '').trim();
    const sentence = (summary?.summary_one_sentence ?? '').trim();
    return paragraph && paragraph !== sentence ? paragraph : '';
  }
}
